import { useState, useRef, useCallback, useEffect } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Upload, Search, X, Loader2, RefreshCw } from 'lucide-react';
import { useEditorStore } from '../../store/editorStore';
import { mediaApi, pexelsApi, coverrApi } from '../../api/client';

const GRADIENT_PRESETS = [
  { name: 'Midnight', stops: [{ color: '#0f0c29', position: 0 }, { color: '#302b63', position: 50 }, { color: '#24243e', position: 100 }], direction: 180 },
  { name: 'Sunset', stops: [{ color: '#f7971e', position: 0 }, { color: '#ffd200', position: 100 }], direction: 135 },
  { name: 'Ocean', stops: [{ color: '#2193b0', position: 0 }, { color: '#6dd5ed', position: 100 }], direction: 135 },
  { name: 'Rose', stops: [{ color: '#f953c6', position: 0 }, { color: '#b91d73', position: 100 }], direction: 135 },
  { name: 'Forest', stops: [{ color: '#134e5e', position: 0 }, { color: '#71b280', position: 100 }], direction: 135 },
  { name: 'Dusk', stops: [{ color: '#4568dc', position: 0 }, { color: '#b06ab3', position: 100 }], direction: 135 },
  { name: 'Fire', stops: [{ color: '#cb2d3e', position: 0 }, { color: '#ef473a', position: 100 }], direction: 180 },
  { name: 'Gold', stops: [{ color: '#f7971e', position: 0 }, { color: '#ffd200', position: 50 }, { color: '#f7971e', position: 100 }], direction: 90 },
  { name: 'Night', stops: [{ color: '#0f2027', position: 0 }, { color: '#203a43', position: 50 }, { color: '#2c5364', position: 100 }], direction: 180 },
  { name: 'Candy', stops: [{ color: '#ff6a88', position: 0 }, { color: '#ff99ac', position: 50 }, { color: '#ffecd2', position: 100 }], direction: 135 },
];

function presetToCss(preset) {
  const stops = preset.stops.map((s) => `${s.color} ${s.position}%`).join(', ');
  return `linear-gradient(${preset.direction}deg, ${stops})`;
}

// ── Background Media Picker ───────────────────────────────────

function BgMediaPicker({ bgType, onSelect }) {
  const [tab, setTab] = useState('library');
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Library
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [libraryLoaded, setLibraryLoaded] = useState(false);

  // Stock — page state lives here so scroll can append
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [stockPage, setStockPage] = useState(1);
  const [hasMoreStock, setHasMoreStock] = useState(true);

  const fileInputRef = useRef(null);
  const story = useEditorStore((s) => s.story);

  // Pass query explicitly to avoid stale-closure bugs
  const loadLibrary = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const result = await mediaApi.list({ type: bgType, search: q || undefined });
      const items = result?.items ?? result ?? [];
      setLibraryAssets(items.map((a) => ({ ...a, id: a.id ?? a._id })));
      setLibraryLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [bgType]);

  // append=true → infinite scroll; append=false → fresh search / tab switch
  const loadStock = useCallback(async (q, page = 1, append = false) => {
    if (page === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      if (bgType === 'image') {
        const result = q ? await pexelsApi.search(q, page, 20) : await pexelsApi.popular(page);
        const newPhotos = result.photos || [];
        setPhotos((prev) => append ? [...prev, ...newPhotos] : newPhotos);
        setHasMoreStock(newPhotos.length >= 20);
      } else {
        const result = q ? await coverrApi.search(q, page, 20) : await coverrApi.popular(page);
        const newVideos = result.videos || [];
        setVideos((prev) => append ? [...prev, ...newVideos] : newVideos);
        setHasMoreStock(newVideos.length >= 20);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [bgType]);

  // Load library on mount (and when bgType changes)
  useEffect(() => { loadLibrary(''); }, [loadLibrary]);

  const handleTabChange = (t) => {
    setTab(t);
    const currentStock = bgType === 'image' ? photos : videos;
    if (t === 'library' && !libraryLoaded) loadLibrary(query);
    if (t === 'stock' && currentStock.length === 0) loadStock(query, 1, false);
  };

  async function handleUpload(files) {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const asset = await mediaApi.upload(files[0], story?.id);
      onSelect(asset.url);
    } finally {
      setUploading(false);
    }
  }

  function handleSearch(e) {
    e.preventDefault();
    const q = draft.trim();
    setQuery(q);
    setStockPage(1);
    setHasMoreStock(true);
    if (tab === 'library') loadLibrary(q);
    else loadStock(q, 1, false);
  }

  function handleClear() {
    setDraft('');
    setQuery('');
    setStockPage(1);
    setHasMoreStock(true);
    if (tab === 'library') loadLibrary('');
    else loadStock('', 1, false);
  }

  function handleGridScroll(e) {
    if (tab !== 'stock' || !hasMoreStock || loadingMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 32) {
      const next = stockPage + 1;
      setStockPage(next);
      loadStock(query, next, true);
    }
  }

  const stockItems = bgType === 'image' ? photos : videos;

  return (
    <div style={{ border: '1px solid var(--t-border)', borderRadius: 8, overflow: 'hidden', marginTop: 6 }}>
      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--t-border)' }}>
        {['library', 'stock'].map((t) => (
          <button
            key={t}
            onClick={() => handleTabChange(t)}
            style={{
              flex: 1, padding: '6px 4px', fontSize: 10, fontWeight: 600,
              background: 'transparent', cursor: 'pointer',
              color: tab === t ? 'var(--brand-primary)' : 'var(--t-muted)',
              borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              borderBottom: tab === t ? '2px solid var(--brand-primary)' : '2px solid transparent',
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}
          >
            {t === 'library' ? 'My Library' : 'Stock'}
          </button>
        ))}
        {/* Upload button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          title="Upload file"
          style={{
            padding: '6px 10px', background: 'transparent', cursor: 'pointer',
            borderTop: 'none', borderLeft: '1px solid var(--t-border)', borderRight: 'none', borderBottom: 'none',
            color: 'var(--t-muted)', display: 'flex', alignItems: 'center',
          }}
        >
          {uploading ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={13} />}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={bgType === 'image' ? 'image/*' : 'video/*'}
          style={{ display: 'none' }}
          onChange={(e) => handleUpload(e.target.files)}
        />
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} style={{ padding: '6px 8px', borderBottom: '1px solid var(--t-border)', display: 'flex', gap: 4 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={11} style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', color: 'var(--t-muted)', pointerEvents: 'none' }} />
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={tab === 'library' ? 'Filter...' : 'Search...'}
            style={{
              width: '100%', paddingLeft: 24, paddingRight: 6, paddingTop: 4, paddingBottom: 4,
              fontSize: 11, borderRadius: 5, border: '1px solid var(--t-border)',
              background: 'var(--t-panel)', color: 'var(--t-text)', outline: 'none',
            }}
          />
        </div>
        <button
          type="submit"
          style={{ padding: '4px 7px', borderRadius: 5, border: '1px solid var(--t-border)', background: 'var(--t-panel)', cursor: 'pointer', color: 'var(--t-muted)', display: 'flex', alignItems: 'center' }}
        >
          {loading ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={11} />}
        </button>
        <button
          type="button"
          onClick={handleClear}
          style={{ padding: '4px 7px', borderRadius: 5, border: '1px solid var(--t-border)', background: 'var(--t-panel)', cursor: 'pointer', color: 'var(--t-muted)', display: 'flex', alignItems: 'center' }}
        >
          <RefreshCw size={11} />
        </button>
      </form>

      {/* Grid */}
      <div style={{ maxHeight: 200, overflowY: 'auto', padding: 6 }} onScroll={handleGridScroll}>
        {loading && stockItems.length === 0 && libraryAssets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Loader2 size={16} style={{ color: 'var(--t-muted)', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
          </div>
        ) : tab === 'library' ? (
          libraryAssets.length === 0 ? (
            <p style={{ fontSize: 11, color: 'var(--t-muted)', textAlign: 'center', padding: '16px 0' }}>No {bgType}s in library</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
              {libraryAssets.map((asset) => (
                <button
                  key={asset.id}
                  onClick={() => onSelect(asset.url)}
                  style={{ aspectRatio: '16/9', borderRadius: 5, overflow: 'hidden', border: '1.5px solid var(--t-border)', cursor: 'pointer', padding: 0, background: '#18181b', position: 'relative' }}
                >
                  {asset.type === 'video' ? (
                    <video src={asset.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} preload="metadata" muted playsInline />
                  ) : (
                    <img src={asset.thumbnailUrl || asset.thumbnail || asset.url} alt={asset.originalFilename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                  )}
                </button>
              ))}
            </div>
          )
        ) : (
          loading && stockItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Loader2 size={16} style={{ color: 'var(--t-muted)', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
            </div>
          ) : stockItems.length === 0 ? (
            <p style={{ fontSize: 11, color: 'var(--t-muted)', textAlign: 'center', padding: '16px 0' }}>No results</p>
          ) : bgType === 'image' ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                {photos.map((photo) => (
                  <button
                    key={photo.id}
                    onClick={() => onSelect(photo.regular)}
                    style={{ aspectRatio: '16/9', borderRadius: 5, overflow: 'hidden', border: '1.5px solid var(--t-border)', cursor: 'pointer', padding: 0, background: photo.color }}
                  >
                    <img src={photo.thumb} alt={photo.alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                  </button>
                ))}
              </div>
              {loadingMore && (
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <Loader2 size={13} style={{ color: 'var(--t-muted)', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                {videos.map((video) => (
                  <button
                    key={video.id}
                    onClick={() => onSelect(video.hdUrl || video.sdUrl)}
                    style={{ aspectRatio: '16/9', borderRadius: 5, overflow: 'hidden', border: '1.5px solid var(--t-border)', cursor: 'pointer', padding: 0, background: '#18181b' }}
                  >
                    <img src={video.thumbnail} alt={video.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                  </button>
                ))}
              </div>
              {loadingMore && (
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <Loader2 size={13} style={{ color: 'var(--t-muted)', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
}

// ── Page Properties ───────────────────────────────────────────

export default function PageProperties({ page }) {
  const updatePageBackground = useEditorStore((s) => s.updatePageBackground);
  const updatePageTransition = useEditorStore((s) => s.updatePageTransition);
  const updatePageName = useEditorStore((s) => s.updatePageName);
  const updatePageAutoAdvanceDuration = useEditorStore((s) => s.updatePageAutoAdvanceDuration);
  const [colorOpen, setColorOpen] = useState(false);
  const [stopColorOpenIdx, setStopColorOpenIdx] = useState(null);

  const bg = page.background;

  function updateBg(updates) {
    updatePageBackground(page.id, { ...bg, ...updates });
  }

  function updateGradientStop(index, updates) {
    if (!bg.gradient) return;
    const stops = [...bg.gradient.stops];
    stops[index] = { ...stops[index], ...updates };
    updateBg({ gradient: { ...bg.gradient, stops } });
  }

  function removeGradientStop(index) {
    if (!bg.gradient || (bg.gradient.stops || []).length <= 2) return;
    const stops = (bg.gradient.stops || []).filter((_, i) => i !== index);
    updateBg({ gradient: { ...bg.gradient, stops } });
  }

  function addGradientStop() {
    if (!bg.gradient) return;
    const stops = [...bg.gradient.stops, { color: '#ffffff', position: 75 }];
    updateBg({ gradient: { ...bg.gradient, stops } });
  }

  function applyPreset(preset) {
    updateBg({
      type: 'gradient',
      gradient: {
        type: 'linear',
        direction: preset.direction,
        stops: preset.stops,
      },
    });
  }

  return (
    <div className="p-2 space-y-3">
      <div>
        <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Page Name</label>
        <input
          type="text"
          value={page.name || ''}
          onChange={(e) => updatePageName(page.id, e.target.value)}
          className="input-field text-xs"
          placeholder="Page name..."
        />
      </div>

      <div>
        <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Background Type</label>
        <select
          value={bg.type}
          onChange={(e) => updateBg({ type: e.target.value })}
          className="input-field text-xs"
        >
          <option value="color">Solid Color</option>
          <option value="gradient">Gradient</option>
          <option value="image">Image</option>
          <option value="video">Video</option>
        </select>
      </div>

      {bg.type === 'color' && (
        <div>
          <label className="text-xs block mb-1" style={{ color: 'var(--t-muted)' }}>Color</label>
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded cursor-pointer flex-shrink-0"
              style={{ background: bg.color || '#000', border: '1px solid var(--t-border2)' }}
              onClick={() => setColorOpen(!colorOpen)}
            />
            <input
              type="text" value={bg.color || '#000000'}
              onChange={(e) => updateBg({ color: e.target.value })}
              className="input-field text-xs py-1 font-mono flex-1"
            />
          </div>
          {colorOpen && (
            <div className="mt-2">
              <HexColorPicker
                color={bg.color || '#000000'}
                onChange={(c) => updateBg({ color: c })}
                style={{ width: '100%', height: 120 }}
              />
            </div>
          )}
        </div>
      )}

      {bg.type === 'gradient' && (
        <div className="space-y-3">
          {/* Preset swatches */}
          <div>
            <label className="text-xs block mb-1.5" style={{ color: 'var(--t-muted)' }}>Presets</label>
            <div className="grid grid-cols-5 gap-1.5">
              {GRADIENT_PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => applyPreset(preset)}
                  title={preset.name}
                  className="rounded-md transition-transform hover:scale-110"
                  style={{
                    height: 32,
                    background: presetToCss(preset),
                    border: '1.5px solid var(--t-border)',
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--brand-primary)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--t-border)'; }}
                />
              ))}
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid var(--t-border)', paddingTop: 8 }}>
            <label className="text-xs font-medium block mb-2" style={{ color: 'var(--t-text)' }}>Custom Gradient</label>

            {/* Current gradient preview */}
            {bg.gradient && (
              <div
                className="rounded-md mb-2"
                style={{
                  height: 32,
                  background: bg.gradient.type === 'radial'
                    ? `radial-gradient(circle, ${(bg.gradient.stops || []).map((s) => `${s.color} ${s.position}%`).join(', ')})`
                    : `linear-gradient(${bg.gradient.direction}deg, ${(bg.gradient.stops || []).map((s) => `${s.color} ${s.position}%`).join(', ')})`,
                  border: '1px solid var(--t-border)',
                }}
              />
            )}

            <div className="grid grid-cols-2 gap-1.5 mb-2">
              <div>
                <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Type</label>
                <select
                  value={bg.gradient?.type ?? 'linear'}
                  onChange={(e) => updateBg({ gradient: { ...bg.gradient, type: e.target.value } })}
                  className="input-field text-xs"
                >
                  <option value="linear">Linear</option>
                  <option value="radial">Radial</option>
                </select>
              </div>
              {bg.gradient?.type === 'linear' && (
                <div>
                  <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Direction°</label>
                  <input
                    type="number" value={bg.gradient.direction}
                    onChange={(e) => updateBg({ gradient: { ...bg.gradient, direction: parseInt(e.target.value) } })}
                    min={0} max={360}
                    className="input-field text-xs py-1"
                  />
                </div>
              )}
            </div>

            {/* Color stops */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs" style={{ color: 'var(--t-muted)' }}>Color Stops</label>
                <button onClick={addGradientStop} className="text-xs" style={{ color: 'var(--brand-primary)', background: 'none', border: 'none', cursor: 'pointer' }}>+ Add</button>
              </div>
              {(bg.gradient?.stops || []).map((stop, i) => (
                <div key={i} className="mb-2">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div
                      className="w-6 h-6 rounded cursor-pointer flex-shrink-0"
                      style={{ background: stop.color, border: '1px solid var(--t-border2)' }}
                      onClick={() => setStopColorOpenIdx(stopColorOpenIdx === i ? null : i)}
                    />
                    <input
                      type="text" value={stop.color}
                      onChange={(e) => updateGradientStop(i, { color: e.target.value })}
                      className="input-field text-xs py-1 font-mono flex-1"
                    />
                    <input
                      type="number" value={stop.position}
                      onChange={(e) => updateGradientStop(i, { position: parseInt(e.target.value) })}
                      min={0} max={100}
                      className="input-field text-xs py-1"
                      style={{ width: 42 }}
                    />
                    <span className="text-xs" style={{ color: 'var(--t-muted)' }}>%</span>
                    {bg.gradient && (bg.gradient.stops || []).length > 2 && (
                      <button
                        onClick={() => removeGradientStop(i)}
                        className="text-xs"
                        style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}
                        title="Remove stop"
                      >✕</button>
                    )}
                  </div>
                  {stopColorOpenIdx === i && (
                    <div className="mb-1">
                      <HexColorPicker
                        color={stop.color}
                        onChange={(c) => updateGradientStop(i, { color: c })}
                        style={{ width: '100%', height: 110 }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {(bg.type === 'image' || bg.type === 'video') && (
        <div>
          <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>
            Background {bg.type === 'image' ? 'Image' : 'Video'}
          </label>
          {bg.mediaSrc && (
            <div style={{ position: 'relative', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--t-border)', marginBottom: 6, aspectRatio: '16/9' }}>
              {bg.type === 'image' ? (
                <img src={bg.mediaSrc} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <video src={bg.mediaSrc} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
              )}
              <button
                onClick={() => updateBg({ mediaSrc: '' })}
                title="Remove"
                style={{
                  position: 'absolute', top: 4, right: 4,
                  background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 4,
                  color: '#fff', cursor: 'pointer', padding: '2px 4px', lineHeight: 1,
                  display: 'flex', alignItems: 'center',
                }}
              >
                <X size={11} />
              </button>
            </div>
          )}
          <BgMediaPicker
            bgType={bg.type}
            onSelect={(src) => updateBg({ mediaSrc: src })}
          />
        </div>
      )}

      {/* Overlay */}
      <div>
        <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Overlay Opacity</label>
        <input
          type="range"
          value={(bg.overlay?.opacity ?? 0) * 100}
          onChange={(e) => updateBg({ overlay: { color: bg.overlay?.color || '#000000', opacity: parseInt(e.target.value) / 100 } })}
          min={0} max={100}
          className="w-full"
        />
        <div className="flex justify-between text-xs" style={{ color: 'var(--t-muted)' }}>
          <span>0%</span>
          <span>{Math.round((bg.overlay?.opacity ?? 0) * 100)}%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Transition */}
      <div>
        <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Page Transition</label>
        <select
          value={page.transition?.type || 'none'}
          onChange={(e) => updatePageTransition(page.id, { type: e.target.value })}
          className="input-field text-xs"
        >
          <option value="none">None</option>
          <option value="fade">Fade</option>
          <option value="fly-in-left">Fly In Left</option>
          <option value="fly-in-right">Fly In Right</option>
        </select>
      </div>

      {/* Auto advance */}
      <div>
        <label className="text-xs block mb-0.5" style={{ color: 'var(--t-muted)' }}>Auto-advance (ms, 0 = off)</label>
        <input
          type="number"
          value={page.autoAdvanceDuration ?? ''}
          onChange={(e) => {
            const val = e.target.value ;
            updatePageAutoAdvanceDuration(page.id, isNaN(val) ? 0 : val);
          }}
      
          // step={1000}
          className="input-field text-xs"
          placeholder="0 = manual"
        />
      </div>
    </div>
  );
}
